import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  ElementRef,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

const OVERLAY_Z_INDEX = 11050;

@Component({
  selector: 'app-multiselect',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './multiselect.component.html',
  styleUrl: './multiselect.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiselectComponent),
      multi: true,
    },
  ],
})
export class MultiselectComponent implements ControlValueAccessor, OnInit, OnDestroy, AfterViewInit {
  @Input() options: any[] = [];
  @Input() optionLabel: string | undefined = undefined;
  @Input() placeholder: string = 'Select Items';
  @Input() styleClass: string = '';
  @Input() disabled: boolean = false;
  @Input() showClear: boolean = true;
  @Input() disableUncheck: boolean = false;
  @Input() display: 'comma' | 'chip' = 'chip';
  @Input() filter: boolean = false;
  /** When `'body'`, panel is portaled to document.body so it is not clipped by modals/footers. */
  @Input() appendTo: string | null = 'body';
  /**
   * Maximum number of items that can be selected.
   * 0 or undefined means "no limit". Use 1 for single-select behavior with checkbox UI.
   */
  @Input() maxSelected: number = 0;

  @Output() onChange = new EventEmitter<any>();
  @Output() onOpen = new EventEmitter<void>();
  @Output() onClose = new EventEmitter<void>();
  @Output() onTriggerMousedown = new EventEmitter<MultiselectComponent>();

  onTriggerMousedownHandler(): void {
    this.onTriggerMousedown.emit(this);
  }

  @ViewChild('chipsMeasure') chipsMeasure?: ElementRef<HTMLElement>;
  @ViewChild('triggerElement') triggerElement?: ElementRef<HTMLElement>;
  @ViewChild('panelDropdown') panelDropdown?: ElementRef<HTMLElement>;

  selectedItems: any[] = [];
  isOpen = false;
  searchTerm = '';
  /** When true, show "N selected" instead of individual chips (3+ items, or 2 overflowing items). */
  collapseChipsToSummary = false;

  private onChangeCallback = (value: any[]) => {};
  private onTouchedCallback = () => {};
  private resizeObserver?: ResizeObserver;
  private chipLayoutCheckScheduled = false;
  private dropdownOriginalParent: HTMLElement | null = null;
  private scrollListener: (() => void) | null = null;
  private positionRetryCount = 0;

  private _docClickHandler = (event: MouseEvent): void => {
    this.onDocumentClick(event);
  };

  constructor(
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    document.addEventListener('click', this._docClickHandler, { capture: true });
  }

  ngAfterViewInit(): void {
    const chipArea = this.elementRef.nativeElement.querySelector('.multiselect-chip-area');
    if (chipArea && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.scheduleChipLayoutCheck());
      this.resizeObserver.observe(chipArea);
    }
    this.scheduleChipLayoutCheck();
  }

  ngOnDestroy(): void {
    document.removeEventListener('click', this._docClickHandler, { capture: true });
    this.resizeObserver?.disconnect();
    this.removeScrollListener();
    const el = this.panelDropdown?.nativeElement;
    if (el?.parentNode === document.body) {
      el.parentNode.removeChild(el);
    }
    this.dropdownOriginalParent = null;
  }

  get showChipSummary(): boolean {
    if (this.display !== 'chip' || this.selectedItems.length === 0) {
      return false;
    }
    return this.selectedItems.length >= 3 || this.collapseChipsToSummary;
  }

  get showIndividualChips(): boolean {
    return (
      this.display === 'chip' &&
      this.selectedItems.length > 0 &&
      this.selectedItems.length <= 2 &&
      !this.showChipSummary
    );
  }

  get shouldAppendToBody(): boolean {
    return this.appendTo === 'body';
  }

  private scheduleChipLayoutCheck(): void {
    if (this.chipLayoutCheckScheduled) {
      return;
    }
    this.chipLayoutCheckScheduled = true;
    requestAnimationFrame(() => {
      this.chipLayoutCheckScheduled = false;
      this.updateChipCollapse();
    });
  }

  private updateChipCollapse(): void {
    const prev = this.collapseChipsToSummary;
    if (this.display !== 'chip' || this.selectedItems.length === 0) {
      this.collapseChipsToSummary = false;
    } else if (this.selectedItems.length >= 3) {
      this.collapseChipsToSummary = true;
    } else if (this.selectedItems.length === 1 || this.maxSelected === 1) {
      // Keep a single selection as a truncatable chip (ellipsis) instead of "1 selected".
      this.collapseChipsToSummary = false;
    } else {
      const el = this.chipsMeasure?.nativeElement;
      if (!el) {
        this.collapseChipsToSummary = false;
      } else {
        this.collapseChipsToSummary = el.scrollWidth > el.clientWidth + 1;
      }
    }
    if (prev !== this.collapseChipsToSummary) {
      this.cdr.markForCheck();
    }
  }

  /** Capture phase so we still run when sibling triggers use stopPropagation / filter-panel stops bubbling. */
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node;
    const host = this.elementRef.nativeElement as HTMLElement;
    const panel = this.panelDropdown?.nativeElement;

    if (host.contains(target) || (panel && panel.contains(target))) {
      return;
    }
    this.closeDropdown();
  }

  toggleDropdown(): void {
    if (!this.disabled) {
      const wasOpen = this.isOpen;
      if (wasOpen) {
        this.teardownPanel();
        this.isOpen = false;
        this.onClose.emit();
      } else {
        this.isOpen = true;
        this.onTouchedCallback();
        this.onOpen.emit();
        this.schedulePositionPanel();
      }
    }
  }

  closeDropdown(): void {
    if (this.isOpen) {
      this.teardownPanel();
      this.isOpen = false;
      this.onClose.emit();
      this.cdr.markForCheck();
    }
  }

  getDisplayValue(): string {
    if (this.selectedItems.length === 0) {
      return this.placeholder;
    }

    if (this.display === 'chip') {
      return `${this.selectedItems.length} selected`;
    }

    return this.selectedItems.map((item) => this.getOptionLabel(item)).join(', ');
  }

  getOptionLabel(option: any): string {
    if (this.optionLabel && typeof option === 'object' && option !== null) {
      return option[this.optionLabel] || String(option);
    }
    return String(option);
  }

  getOptionValue(option: any): any {
    return option;
  }

  isSelected(option: any): boolean {
    return this.selectedItems.some((item) => {
      if (this.optionLabel && typeof item === 'object' && typeof option === 'object') {
        return item[this.optionLabel] === option[this.optionLabel];
      }
      return item === option;
    });
  }

  toggleSelection(option: any): void {
    const optionValue = this.getOptionValue(option);
    const index = this.selectedItems.findIndex((item) => {
      if (this.optionLabel && typeof item === 'object' && typeof optionValue === 'object') {
        return item[this.optionLabel] === optionValue[this.optionLabel];
      }
      return item === optionValue;
    });

    if (index > -1) {
      if (this.disableUncheck) {
        return;
      }
      this.selectedItems.splice(index, 1);
    } else {
      if (this.maxSelected === 1) {
        this.selectedItems = [optionValue];
      } else if (this.maxSelected > 1 && this.selectedItems.length >= this.maxSelected) {
        this.selectedItems.shift();
        this.selectedItems.push(optionValue);
      } else {
        this.selectedItems.push(optionValue);
      }
    }

    this.onChangeCallback([...this.selectedItems]);
    this.onChange.emit([...this.selectedItems]);
    this.scheduleChipLayoutCheck();

    if (this.maxSelected === 1) {
      this.closeDropdown();
    } else {
      this.schedulePositionPanel();
    }
  }

  get filteredOptions(): any[] {
    if (!this.searchTerm) {
      return this.options;
    }
    const term = this.searchTerm.toLowerCase();
    return this.options.filter((option) => {
      const label = this.getOptionLabel(option).toLowerCase();
      return label.includes(term);
    });
  }

  clearSelection(): void {
    this.selectedItems = [];
    this.onChangeCallback([]);
    this.onChange.emit([]);
    this.scheduleChipLayoutCheck();
  }

  removeChip(item: any, event: Event): void {
    event.stopPropagation();
    const index = this.selectedItems.findIndex((selected) => {
      if (this.optionLabel && typeof selected === 'object' && typeof item === 'object') {
        return selected[this.optionLabel] === item[this.optionLabel];
      }
      return selected === item;
    });

    if (index > -1) {
      this.selectedItems.splice(index, 1);
      this.onChangeCallback([...this.selectedItems]);
      this.onChange.emit([...this.selectedItems]);
      this.scheduleChipLayoutCheck();
    }
  }

  trackByOption(index: number, option: any): any {
    if (this.optionLabel && typeof option === 'object' && option !== null) {
      return option[this.optionLabel] || index;
    }
    return option || index;
  }

  getOptionClasses(option: any): string {
    const baseClasses = 'flex items-center px-3 py-2 cursor-pointer hover:bg-gray-50 transition-colors';
    return baseClasses;
  }

  writeValue(value: any[]): void {
    this.selectedItems = value || [];
    this.scheduleChipLayoutCheck();
  }

  registerOnChange(fn: (value: any[]) => void): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  private schedulePositionPanel(): void {
    if (!this.shouldAppendToBody || !this.isOpen) {
      return;
    }
    this.positionRetryCount = 0;
    // Wait for *ngIf panel + ViewChild to resolve after open
    this.cdr.detectChanges();
    setTimeout(() => this.positionPanel(), 0);
  }

  private positionPanel(): void {
    if (!this.shouldAppendToBody || !this.isOpen) {
      return;
    }

    if (!this.panelDropdown?.nativeElement || !this.triggerElement?.nativeElement) {
      if (this.positionRetryCount++ < 8) {
        requestAnimationFrame(() => this.positionPanel());
      }
      return;
    }

    const trigger = this.triggerElement.nativeElement;
    const dropdown = this.panelDropdown.nativeElement;
    const triggerRect = trigger.getBoundingClientRect();
    if (triggerRect.width === 0 && triggerRect.height === 0) {
      if (this.positionRetryCount++ < 8) {
        requestAnimationFrame(() => this.positionPanel());
      }
      return;
    }
    this.positionRetryCount = 0;

    const viewportH = window.innerHeight || document.documentElement.clientHeight;
    const viewportW = window.innerWidth || document.documentElement.clientWidth;

    if (dropdown.parentNode !== document.body) {
      this.dropdownOriginalParent = dropdown.parentElement;
      document.body.appendChild(dropdown);
    }

    dropdown.classList.add('multiselect-panel-overlay');

    const spaceBelow = viewportH - triggerRect.bottom;
    const spaceAbove = triggerRect.top;
    // Prefer opening below; only flip above when there truly isn't room under the trigger
    const placeAbove = spaceBelow < 120 && spaceAbove > spaceBelow;

    const width = Math.max(triggerRect.width, 200);
    let left = triggerRect.left;
    if (left + width > viewportW - 8) {
      left = Math.max(8, viewportW - width - 8);
    }
    if (left < 8) {
      left = 8;
    }

    dropdown.style.position = 'fixed';
    dropdown.style.left = `${left}px`;
    dropdown.style.width = `${width}px`;
    dropdown.style.minWidth = `${width}px`;
    dropdown.style.maxWidth = `${width}px`;
    dropdown.style.zIndex = String(OVERLAY_Z_INDEX);
    dropdown.style.right = 'auto';
    dropdown.style.marginTop = '0';

    if (placeAbove) {
      dropdown.style.top = 'auto';
      dropdown.style.bottom = `${viewportH - triggerRect.top + 4}px`;
    } else {
      dropdown.style.bottom = 'auto';
      dropdown.style.top = `${triggerRect.bottom + 4}px`;
    }

    this.removeScrollListener();
    this.scrollListener = () => {
      if (this.isOpen) {
        this.positionPanel();
      }
    };
    window.addEventListener('scroll', this.scrollListener, true);
    window.addEventListener('resize', this.scrollListener, { passive: true });
  }

  private teardownPanel(): void {
    const el = this.panelDropdown?.nativeElement;
    if (el) {
      el.classList.remove('multiselect-panel-overlay');
      el.style.position = '';
      el.style.left = '';
      el.style.top = '';
      el.style.bottom = '';
      el.style.right = '';
      el.style.width = '';
      el.style.minWidth = '';
      el.style.maxWidth = '';
      el.style.zIndex = '';
      el.style.marginTop = '';
      if (el.parentNode === document.body && this.dropdownOriginalParent) {
        this.dropdownOriginalParent.appendChild(el);
      }
    }
    this.dropdownOriginalParent = null;
    this.removeScrollListener();
  }

  private removeScrollListener(): void {
    if (this.scrollListener) {
      window.removeEventListener('scroll', this.scrollListener, true);
      window.removeEventListener('resize', this.scrollListener);
      this.scrollListener = null;
    }
  }
}
